import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DocStarIcon } from 'components/blocks/layout/doc-star-icon';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import {
  CHAT_MESSAGE_ACTION_ICON_CLASS,
} from 'constant/chat-layout';
import { cn } from 'lib/utils';
import type { MessageGeneration } from '../types/chat';
import { groupMessageSkillEventsBySkillId } from '../utils/parse-message-skill-events';

interface MessageSkillEventsProps {
  message: MessageGeneration;
}

function formatSkillEventSummary(
  usedCount: number,
  skippedCount: number,
  resourceLoadedCount: number
): string {
  const parts: string[] = [];
  if (usedCount > 0) parts.push(`${usedCount} used`);
  if (skippedCount > 0) parts.push(`${skippedCount} skipped`);
  if (resourceLoadedCount > 0) parts.push(`${resourceLoadedCount} loaded`);
  return parts.join(' · ');
}

export function MessageSkillEvents({ message }: MessageSkillEventsProps) {
  const { t } = useTranslation();
  const skillEvents = message.skillEvents;
  const [isOpen, setIsOpen] = useState(false);

  const groupedSkillEvents = useMemo(
    () => (skillEvents ? groupMessageSkillEventsBySkillId(skillEvents) : []),
    [skillEvents]
  );

  if (!skillEvents?.length) {
    return null;
  }

  const eventCountLabel = skillEvents.length

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={isOpen} onOpenChange={setIsOpen}>
        <TooltipTrigger asChild>
          <div className="relative inline-block">
            <button
              type="button"
              className="relative inline-flex shrink-0 size-8 items-center justify-center rounded-full p-0 hover:bg-muted"
              onClick={(e) => e.stopPropagation()}
              title={t('MESSAGE_SKILL_EVENTS') || 'Skills'}
              aria-label={t('MESSAGE_SKILL_EVENTS') || 'Skill events'}
            >
              <DocStarIcon
                variant="muted"
                className={cn(CHAT_MESSAGE_ACTION_ICON_CLASS, 'size-5')}
              />
            </button>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="w-[min(100vw-2rem,288px)] p-0">
          <div className="flex flex-col overflow-hidden rounded-md">
            <div className="flex items-center justify-between gap-3 border-b border-primary-foreground/20 px-3 py-2">
              <span className="text-xs font-semibold">
                {t('MESSAGE_SKILL_EVENTS') || 'Skill activity'}
              </span>
              <span className="text-[10px] opacity-70">{eventCountLabel}</span>
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 border-b border-primary-foreground/10 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide opacity-60">
              <span>{t('SKILLS') || 'Skills'}</span>
              <span className="text-right">{t('SKILL_EVENT_SUMMARY') || 'Activity'}</span>
            </div>

            <div className="max-h-[min(50vh,220px)] overflow-y-auto overscroll-contain">
              {groupedSkillEvents.map((event, index) => (
                <div
                  key={event.skillId}
                  className={cn(
                    'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 px-3 py-2 text-xs',
                    index % 2 === 1 && 'bg-primary-foreground/5',
                    index < groupedSkillEvents.length - 1 &&
                    'border-b border-primary-foreground/10'
                  )}
                >
                  <span className="truncate font-medium" title={event.skillName}>
                    {event.skillName}
                  </span>
                  <span className="shrink-0 text-right text-[11px] opacity-90">
                    {formatSkillEventSummary(
                      event.usedCount,
                      event.skippedCount,
                      event.resourceLoadedCount
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
